import type { CSSProperties } from "react";
import { IMPACT, PROBABILITY, levelOf, type MatrixCell } from "./api";

const BG: Record<string, string> = { baixo: "var(--ok-bg)", moderado: "var(--info-bg)", alto: "var(--warn-bg)", critico: "var(--crit-bg)" };
const BORDER: Record<string, string> = { baixo: "var(--ok-border)", moderado: "var(--info-border)", alto: "var(--warn-border)", critico: "var(--crit-border)" };
const FG: Record<string, string> = { baixo: "var(--ok)", moderado: "var(--info)", alto: "var(--warn)", critico: "var(--crit)" };

export type Cell = { probability: number; impact: number } | null;

/** Grade 5 × 5: probabilidade nas linhas (5 no topo), impacto nas colunas. */
export function RiskMatrix({ cells, selected, onSelect, compact }: { cells: MatrixCell[]; selected?: Cell; onSelect?: (c: Cell) => void; compact?: boolean }) {
  const at = (p: number, i: number) => cells.find((c) => c.probability === p && c.impact === i);
  const axis: CSSProperties = { fontSize: "var(--fs-11)", color: "var(--text-2)", display: "flex", alignItems: "center" };
  return (
    <div role="group" aria-label="Matriz de riscos: probabilidade por impacto">
      <div style={{ display: "grid", gridTemplateColumns: `${compact ? 20 : 92}px repeat(5, minmax(0, 1fr))`, gap: 4 }}>
        {[5, 4, 3, 2, 1].map((p) => (
          <div key={p} style={{ display: "contents" }}>
            <div style={{ ...axis, justifyContent: "flex-end", paddingRight: 6, textAlign: "right" }} title={PROBABILITY[p]}>
              {compact ? p : <span><span className="num">{p}</span> {PROBABILITY[p]}</span>}
            </div>
            {[1, 2, 3, 4, 5].map((i) => {
              const c = at(p, i);
              const level = levelOf(p * i);
              const isSel = selected?.probability === p && selected?.impact === i;
              const n = c?.risks ?? 0;
              const label = `Probabilidade ${p} (${PROBABILITY[p]}) e impacto ${i} (${IMPACT[i]}): ${n} risco(s)${n ? ` (${c!.codes.join(", ")})` : ""}`;
              return (
                <button
                  key={i}
                  type="button"
                  aria-label={label}
                  aria-pressed={isSel}
                  title={label}
                  disabled={!onSelect || n === 0}
                  onClick={() => onSelect?.(isSel ? null : { probability: p, impact: i })}
                  style={{
                    minHeight: compact ? 34 : 58,
                    border: `1px solid ${isSel ? FG[level] : BORDER[level]}`,
                    outline: isSel ? `2px solid ${FG[level]}` : undefined,
                    outlineOffset: -2,
                    background: BG[level],
                    borderRadius: "var(--radius-sm)",
                    padding: compact ? 2 : "6px 8px",
                    textAlign: "left",
                    cursor: onSelect && n ? "pointer" : "default",
                    color: "var(--text)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: compact ? "center" : "stretch",
                    font: "inherit",
                    opacity: n ? 1 : 0.55,
                  }}
                >
                  <span className="num" style={{ fontWeight: 600, fontSize: compact ? "var(--fs-12)" : "var(--fs-16)", color: n ? FG[level] : "var(--text-3)" }}>{n || ""}</span>
                  {!compact && n ? <span className="num" style={{ fontSize: "var(--fs-11)", color: "var(--text-2)", lineHeight: 1.3, whiteSpace: "normal", overflowWrap: "anywhere" }}>{c!.codes.length > 4 ? `${c!.codes.slice(0, 3).join(" ")} +${c!.codes.length - 3}` : c!.codes.join(" ")}</span> : null}
                </button>
              );
            })}
          </div>
        ))}
        <div />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ ...axis, justifyContent: "center", textAlign: "center", paddingTop: 2 }} title={IMPACT[i]}>
            {compact ? i : <span><span className="num">{i}</span> {IMPACT[i]}</span>}
          </div>
        ))}
      </div>
      <div className="vx-row" style={{ justifyContent: "space-between", marginTop: 6, fontSize: "var(--fs-11)", color: "var(--text-2)", gap: 8 }}>
        <span style={{ paddingLeft: compact ? 0 : 92 }}>Linhas: probabilidade · colunas: impacto</span>
        <span className="vx-row" style={{ gap: 10 }}>
          {(["baixo", "moderado", "alto", "critico"] as const).map((l) => (
            <span key={l} className="vx-row" style={{ gap: 4, flexWrap: "nowrap" }}>
              <span aria-hidden style={{ width: 10, height: 10, borderRadius: 2, background: BG[l], border: `1px solid ${BORDER[l]}` }} />
              {l === "critico" ? "crítico" : l}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}
